import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import type { PermsSerialized, AuthenticatorConfigSerialized } from '@playq/octopus2-auth';

import { systemRehydrate } from '/store/systemActions';

import { login, logout, fetchCompanyPerms } from './actions';
import * as selectors from './selectors';
import { initialState } from './state';
import type { IAuthState, IUserInfo } from './types';

const { baseSelectors, ...derivedSelectors } = selectors;
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateUser: (state, action: PayloadAction<IUserInfo>) => {
      state.user = { ...state.user, ...action.payload };
    },

    rehydrate: (state, action: PayloadAction<IAuthState>) => {
      _.merge(state, action.payload);
    },

    reset: (state) => {
      _.merge(state, initialState, { googleRedirectURL: state.googleRedirectURL });
    },

    getPermsByCompany: (state, action: PayloadAction<PermsSerialized>) => {
      state.companyPerms = action.payload;
    },

    updateRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },

    updateGoogleRedirectURL: (state, action: PayloadAction<string | undefined>) => {
      state.googleRedirectURL = action.payload;
    },

    updateLoginRedirectURL: (state, action: PayloadAction<string | undefined>) => {
      const url = action.payload;
      if (url !== '/login' && url !== '/auth/google') {
        state.loginRedirectURL = url;
      }
    },

    updateServicesAuthorizationStatus: (state, action: PayloadAction<boolean>) => {
      state.isServicesAuthorizationSet = action.payload;
    },

    updateMfa: (state, action: PayloadAction<{ [key: string]: AuthenticatorConfigSerialized } | undefined>) => {
      state.mfa = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, { payload }) => {
      state.user = payload.user;
      state.tokens = payload.tokens;
      state.loggedIn = true;
      state.isServicesAuthorizationSet = true;
      state.mfa = payload.mfa;
    });

    builder.addCase(login.pending, (state, action) => {
      _.merge(state, {
        loggedIn: true,
        ...action.meta.arg.serialize(),
      });
    });
    builder.addCase(logout.pending, (state) => {
      const { rememberMe, googleRedirectURL, loginRedirectURL } = state;
      _.merge(state, initialState);
      state.rememberMe = rememberMe;
      state.googleRedirectURL = googleRedirectURL;
      state.loginRedirectURL = loginRedirectURL;
      state.loggedIn = false;
    });
    builder.addCase(logout.fulfilled, (state, { payload }) => {
      if (payload?.loginRedirectURL) {
        state.loginRedirectURL = payload.loginRedirectURL;
      }
    });

    builder.addCase(systemRehydrate.type, (state) => {
      state.pending = true;
    });

    builder.addCase(fetchCompanyPerms.fulfilled, (state, { payload }) => {
      state.companyPerms = payload;
    });
  },
});
export const {
  updateUser,
  rehydrate,
  reset,
  getPermsByCompany,
  updateRememberMe,
  updateGoogleRedirectURL,
  updateLoginRedirectURL,
  updateServicesAuthorizationStatus,
  updateMfa,
} = authSlice.actions;

export const authToolkit = Object.freeze({
  initialState,
  actions: {
    ...authSlice.actions,
    login,
    logout,
    fetchCompanyPerms,
  },
  selectors: {
    ...baseSelectors,
    ...derivedSelectors,
  },
});

export const authReducer = authSlice.reducer;
